import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from 'lib/api/apiClient';
import { SEARCH_URLS } from 'lib/api/endpoints';

export const checkFeasibility = createAsyncThunk(
  'tasking/checkFeasibility',
  async ({ coordinates, constellations, dateRange, constraints }, thunkAPI) => {
    try {
      const formattedCoordinates = coordinates.map((coord) => [coord.lng, coord.lat]);
      const dateString = `${dateRange.startDate}/${dateRange.endDate}`;
      const payload = {
        coordinates: formattedCoordinates,
        constellations,
        date: dateString,
        constraints: constraints || {},
      };
      const response = await apiClient.post(SEARCH_URLS.checkFeasibility, payload, {
        signal: thunkAPI.signal,
      });
      // response.data.data is keyed by constellation name
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        error: error.response?.data?.detail || 'Feasibility check failed',
      });
    }
  }
);

const taskingSlice = createSlice({
  name: 'tasking',
  initialState: {
    taskingDetails: {},
    constellations: [],
    checkedConstellations: {},
    stripSelections: {},
    footprintsByConstellation: {},
    visibleConstellations: {},
    loading: false,
    isLoadingProducts: false,
    loadingConstellations: [],
    error: null,
    rawProducts: [],
    taskingFilters: {
      resolutionRange: [0, 3],
      maxOffNadirAngle: 25,
      maxSunElevation: 0,
      sensorType: ['optical'],
    },
  },
  reducers: {
    setConstellations(state, action) {
      state.constellations = action.payload;
    },
    setCheckedConstellations(state, action) {
      state.checkedConstellations = action.payload;
    },
    toggleConstellationChecked(state, action) {
      const name = action.payload;
      state.checkedConstellations[name] = !state.checkedConstellations[name];
    },
    markConstellationChecked(state, action) {
      state.checkedConstellations[action.payload] = true;
    },
    setTaskingDetails(state, action) {
      state.taskingDetails = action.payload;
    },
    updateTaskingDetail(state, action) {
      const { constellation, details } = action.payload;
      state.taskingDetails[constellation] = details;
    },
    setStripSelections(state, action) {
      state.stripSelections = action.payload;
    },
    updateStripSelection(state, action) {
      const { constellation, strips } = action.payload;
      state.stripSelections[constellation] = strips;
    },
    setFootprintsByConstellation(state, action) {
      state.footprintsByConstellation = action.payload;
    },
    updateFootprints(state, action) {
      const { constellation, footprints } = action.payload;
      state.footprintsByConstellation[constellation] = footprints;
    },
    removeConstellationFootprints(state, action) {
      const constellation = action.payload;
      delete state.footprintsByConstellation[constellation];
    },
    setVisibleConstellations(state, action) {
      state.visibleConstellations = action.payload;
    },
    updateVisibleConstellation(state, action) {
      const { name, visible } = action.payload;
      state.visibleConstellations[name] = visible;
    },
    addLoadingConstellation(state, action) {
      const name = action.payload;
      if (!state.loadingConstellations.includes(name)) {
        state.loadingConstellations.push(name);
      }
    },
    removeLoadingConstellation(state, action) {
      const name = action.payload;
      state.loadingConstellations = state.loadingConstellations.filter((c) => c !== name);
    },
    setTaskingError(state, action) {
      state.error = action.payload;
    },
    setIsLoadingProducts(state, action) {
      state.isLoadingProducts = action.payload;
    },
    setRawProducts(state, action) {
      state.rawProducts = action.payload;
    },
    setTaskingFilter(state, action) {
      const { key, value } = action.payload;
      state.taskingFilters[key] = value;
    },
    resetTaskingFilters(state) {
      state.taskingFilters = {
        resolutionRange: [0, 3],
        maxOffNadirAngle: 25,
        maxSunElevation: 0,
        sensorType: ['optical'],
      };
    },
    clearTaskingResults(state) {
      state.taskingDetails = {};
      state.checkedConstellations = {};
      state.stripSelections = {};
      state.footprintsByConstellation = {};
      state.visibleConstellations = {};
      state.isLoadingProducts = false;
      state.loadingConstellations = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkFeasibility.pending, (state, action) => {
        const constellations = action.meta.arg.constellations || [];
        constellations.forEach((name) => {
          if (!state.loadingConstellations.includes(name)) {
            state.loadingConstellations.push(name);
          }
        });
        state.error = null;
      })
      .addCase(checkFeasibility.fulfilled, (state, action) => {
        // action.payload is { "KOMPSAT-3": {...}, "SPOT-6": {...}, ... }
        const resultsByConstellation = action.payload;
        // Clear stale selections/footprints from previous results
        state.stripSelections = {};
        state.footprintsByConstellation = {};
        state.visibleConstellations = {};
        state.taskingDetails = {};
        Object.entries(resultsByConstellation).forEach(([name, data]) => {
          state.taskingDetails[name] = data;
        });
        state.loadingConstellations = [];
      })
      .addCase(checkFeasibility.rejected, (state, action) => {
        if (action.meta.aborted) return;
        const { error } = action.payload || {};
        state.loadingConstellations = [];
        state.error = error || 'Feasibility check failed';
      });
  },
});

export const {
  setConstellations,
  setCheckedConstellations,
  toggleConstellationChecked,
  markConstellationChecked,
  setTaskingDetails,
  updateTaskingDetail,
  setStripSelections,
  updateStripSelection,
  setFootprintsByConstellation,
  updateFootprints,
  removeConstellationFootprints,
  setVisibleConstellations,
  updateVisibleConstellation,
  addLoadingConstellation,
  removeLoadingConstellation,
  setTaskingError,
  setIsLoadingProducts,
  setRawProducts,
  setTaskingFilter,
  resetTaskingFilters,
  clearTaskingResults,
} = taskingSlice.actions;
export default taskingSlice.reducer;
